import moment from 'moment';
import { SQUAD_COLORS, SQUAD_DEFAULT_ISIN } from './templateConstants';
import { SQUAD_FS_HEADER_SECONDARY } from './squadCustomPortfolio';

export type RollingPeriod = {
  label: string;
  /** Manual dual-series (brutto/netto). */
  brutto?: number;
  netto?: number;
  /** Legacy single-series (SHV Manual). */
  value?: number;
};

export type RollingPerformanceConfig = {
  chartType: 'Automated' | 'Manual';
  periods: RollingPeriod[];
  isin: string;
  automationType: string;
  legend: Record<string, string>;
  barColorSets: string[];
};

const DEFAULT_LEGEND: RollingPerformanceConfig['legend'] = {
  gross_perf_year_as_of_today: 'brutto',
  perf_year_as_of_today: 'netto (mit 5% Ausgabeaufschlag)',
};

/** Fallback only — live chart uses FS-Header primary (brutto) / secondary (netto). */
export const SQUAD_ROLLING_BAR_COLORS = [SQUAD_COLORS.navy, SQUAD_FS_HEADER_SECONDARY];

export const DEFAULT_ROLLING_PERFORMANCE: RollingPerformanceConfig = {
  chartType: 'Automated',
  periods: [],
  isin: SQUAD_DEFAULT_ISIN,
  automationType: 'all',
  legend: { ...DEFAULT_LEGEND },
  barColorSets: [...SQUAD_ROLLING_BAR_COLORS],
};

/**
 * Normalize rolling / rollierende Wertentwicklung config.
 * Defaults to Automated IT4Funds feed (same as SHV), with SQUAD brutto/netto legend + colors.
 *
 * The bars follow the fund ISIN (`historyIsin`), like every other dynamic value. A stored
 * `rollingPerformance.isin` only counts when no fund ISIN is passed: it used to win, so
 * SQUAD-TEST (Green Balance) printed the Value A bars, and every clone of a master would
 * have kept the master's bars.
 */
export function normalizeRollingPerformance(
  rollingPerformance: Partial<RollingPerformanceConfig> | null | undefined,
  fundIsin?: string,
): RollingPerformanceConfig {
  const isin = (
    String(fundIsin || '').trim() ||
    String(rollingPerformance?.isin || '').trim() ||
    SQUAD_DEFAULT_ISIN
  ).toUpperCase();
  const legend = {
    ...DEFAULT_LEGEND,
    ...(rollingPerformance?.legend || {}),
  };
  const configuredColors = rollingPerformance?.barColorSets;
  const barColorSets =
    Array.isArray(configuredColors) && configuredColors.length > 0
      ? configuredColors
      : [...SQUAD_ROLLING_BAR_COLORS];

  const base: RollingPerformanceConfig = {
    chartType: 'Automated',
    periods: [],
    isin,
    automationType: rollingPerformance?.automationType || 'all',
    legend,
    barColorSets,
  };

  if (
    rollingPerformance?.chartType === 'Manual' &&
    Array.isArray(rollingPerformance.periods) &&
    rollingPerformance.periods.length > 0
  ) {
    return {
      ...base,
      chartType: 'Manual',
      periods: rollingPerformance.periods,
    };
  }

  return base;
}

/**
 * Periods that still fit on one line each at the 9 px axis size. "30.09.21 bis 30.09.22"
 * is 82.2 px wide in Segoe UI (86.6 px in Helvetica); a category is 100 px wide at 5
 * periods and 84 px at 6, too tight to rely on. IT4Funds delivers at most 5 (every
 * snapshot in cixon_monthly, 30.10.2026), but manual periods have no limit. Split as
 * "30.09.21 bis" / "30.09.22" (47 px), labels fit up to 9 periods.
 */
export const SQUAD_ROLLING_SINGLE_LINE_MAX_PERIODS = 5;

/** x-axis label of a rolling period, short dates; two lines once the categories get narrow. */
export function formatRollingPeriodAxisLabel(
  value: string,
  periodCount: number,
): string | string[] {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';
  const parts = trimmed.split(/\s+bis\s+/i).map((part) => part.trim());
  if (parts.length !== 2) return trimmed;
  const short = (date: string) => {
    const m = moment(date, ['DD.MM.YYYY', 'DD.MM.YY'], true);
    return m.isValid() ? m.format('DD.MM.YY') : date;
  };
  const [from, to] = parts.map(short);
  return periodCount > SQUAD_ROLLING_SINGLE_LINE_MAX_PERIODS
    ? [`${from} bis`, to]
    : `${from} bis ${to}`;
}
